import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import SectionHeader from "@cayuse/shared-react.components.section-header";
import _ from "lodash";

import { notify } from "@cayuse/shared-react.components.banner-notification";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import PaddedCell from "@cayuse/shared-react.components.padded-cell";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";

import { ModelerPreview } from "containers/Form/Common/ModelerPreview";
import {
	findElementsWithRules,
	findTabComponent,
	getFullModelJson,
	getPreparedModelJson,
	removeDeletedRuleGroupsFromModelJson,
} from "containers/Form/SimplifiedBuilder/utils";
import { useComponentDidMount, useDictionary } from "hooks";
import { updateFormWithLastPublishedFormElements } from "store/actions/form";
import {
	checkPublishedFormElements,
	getLastPublishedFormElements,
	removeElementWithTypeAndReturnSection,
	transformFetchedFormModel,
} from "utils/forms";
import { notifyError, spUrl } from "utils";

import ModelerToolbar from "../Common/ModelerToolbar";
import TabsComponent from "./Components/TabsComponent";
import DragLayer from "./Internal/DragLayer";
import SimplifiedFormBuilderPalette from "./SimplifiedFormBuilderPalette";
import SimplifiedFormBuilderTabList from "./SimplifiedFormBuilderTabList";
import styles from "./SimplifiedFormBuilderView.css";
import { api } from "services";
import { FORMS_BASE_ROUTE } from "../../../globals/constants";
import { Route, Switch } from "react-router-dom";

function SimplifiedFormBuilderView (props) {
	const [ modelJson, setModelJson ] = useState(null);
	const [ shouldUpdateModelJson, setShouldUpdateModelJson ] = useState(false);
	const [ currentTab, selectTab ] = useState(0);
	const [ onPaletteDropEndFunction, setOnPaletteDropEndFunction ] = useState({
		end () {
		}
	});
	const formId = props.match.params.formId;
	const history = props.history;
	const path = props.match.path
	const [ formModelId, setFormModelId ] = useState(formId);
	const [ isSaveBtnDisabled, setIsSaveBtnDisabled ] = useState(true);
	const [ formModelReloaded, setFormModelReloaded ] = useState(false);
	const [ modelKey, setModelKey ] = useState();
	const [ modelName, setModelName ] = useState();
	const [ formElementsWithRulesAssociated, setFormElementsWithRulesAssociated ] = useState([]);
	const [ shouldUpdateRulesArray, setShouldUpdateRulesArray ] = useState(true);
	const [ deletedRuleGroups, setDeletedRuleGroups ] = useState(null);
	const notificationsDictionary = useDictionary("formDesigner.notifications");

	const componentDidMount = useComponentDidMount();

	const fetchModel = useCallback(async formModelId => {
		setFormModelReloaded(false);

		let modelJson = await api.form.getSimplifiedModel(formModelId);
		const lastPublishedElements = await getLastPublishedFormElements(modelJson.metadata.key);
		const lastPublishedIds = lastPublishedElements?.map(element => element.id);

		modelJson = checkPublishedFormElements(lastPublishedIds, modelJson);
		modelJson = transformFetchedFormModel(_.cloneDeep(modelJson));

		const subformSection = removeElementWithTypeAndReturnSection(modelJson, "subform");
		if (subformSection !== null) {
			const message = notificationsDictionary.foundSubformElement.replace("{{sectionLabel}}", subformSection);
			notify(message, { type: "warning", autoClose: false });
		}

		updateFormWithLastPublishedFormElements(lastPublishedIds);
		setModelKey(modelJson.metadata.key);
		setModelName(modelJson.metadata.name);
		setFormModelId(formModelId);
		setModelJson(findTabComponent(modelJson));
		setFormModelReloaded(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (formId && componentDidMount) {
			fetchModel(formId);
		}
	}, [ formId, componentDidMount ]);

	useEffect(() => {
		if (shouldUpdateModelJson && formId) {
			fetchModel(formId);
			setShouldUpdateModelJson(false);
		}
	}, [ formId, shouldUpdateModelJson ]);

	useEffect(() => {
		setIsSaveBtnDisabled(false);
	}, [ modelJson ]);

	useEffect(() => {
		if (formModelReloaded) {
			setIsSaveBtnDisabled(true);
		}
	}, [ formModelReloaded ]);

	useEffect(() => {
		if(isNonEmptyObject(modelJson) && shouldUpdateRulesArray) {
			setShouldUpdateRulesArray(false);
			setFormElementsWithRulesAssociated(findElementsWithRules(modelJson));
		}

		if(isNonEmptyObject(modelJson) && !shouldUpdateRulesArray) {
			const nextElementsWithRules = findElementsWithRules(modelJson);
			const prevElementsWithRules = formElementsWithRulesAssociated;

			if(nextElementsWithRules.length < prevElementsWithRules.length) {
				const ruleGroup = prevElementsWithRules.filter(({ id }) =>
					!nextElementsWithRules.some(elementWithRule => elementWithRule.id === id)
				);
				setDeletedRuleGroups(ruleGroup);
			}
			if(nextElementsWithRules.length !== prevElementsWithRules.length) {
				setShouldUpdateRulesArray(true);
			}
		}
	}, [ formElementsWithRulesAssociated, modelJson, shouldUpdateRulesArray ]);

	useEffect(() => {
		if(deletedRuleGroups !== null) {
			setModelJson(removeDeletedRuleGroupsFromModelJson(modelJson, deletedRuleGroups));
			setDeletedRuleGroups(null);
		}
	}, [ deletedRuleGroups, modelJson ]);

	useEffect(() => {
		const unloadCallback = (event) => {
			if (!isSaveBtnDisabled) {
				event.preventDefault();
				event.returnValue = "";
				return "";
			}
		};

		window.addEventListener("beforeunload", unloadCallback);
		return () => window.removeEventListener("beforeunload", unloadCallback);
	}, [ isSaveBtnDisabled ]);

	const saveDraft = useCallback(async () => {
		try {
			await api.form.updateSimplifiedModel(formModelId, getPreparedModelJson(modelJson));

		} catch (err) {
			notifyError("Something went wrong.", err);
		} finally {
			setIsSaveBtnDisabled(true);
		}
	}, [ formModelId, modelJson ]);

	const fullModelJson = useMemo(
		() => getFullModelJson(modelJson),
		[ modelJson ]
	);

	const onClosePreview = () => {
		history.push(spUrl(`${FORMS_BASE_ROUTE}/${formModelId}`))
		selectTab(0);
	};

	const getInProgressDraft = () => Promise.resolve(modelJson);

	return modelJson ? (
		<>
			<Switch>
				<Route
					exact
					path={`${path}/preview`}
					render={() => <ModelerPreview
						config={fullModelJson}
						closePreview={onClosePreview}
					/>}
				/>
				<Route exact path={path}>
					<DndProvider backend={HTML5Backend}>
						<PaddedCell className={styles.headerContainer}>
							<div className={styles.menuButtonContainer}>
								<SectionHeader header={modelName || "Form"}/>
								<ModelerToolbar
									getInProgressDraft={getInProgressDraft}
									formId={formModelId}
									history={history}
									isSaveBtnDisabled={isSaveBtnDisabled}
									modelKey={modelKey}
									renderPreview={() => history.push(spUrl(`${FORMS_BASE_ROUTE}/${formModelId}/preview`))}
									saveDraft={saveDraft}
									setIsSaveBtnDisabled={setIsSaveBtnDisabled}
									setShouldUpdateModelJson={setShouldUpdateModelJson}
								/>
							</div>
						</PaddedCell>
						<div>
							<div className={styles.palette}>
								<VerticalSpacing>
									<SimplifiedFormBuilderTabList
										id={"$"}
										config={modelJson}
										onTabSelect={index => selectTab(index)}
										onChange={content => setModelJson(content)}
									/>
								</VerticalSpacing>
								<VerticalSpacing>
									<SimplifiedFormBuilderPalette
										onPaletteDropEnd={onPaletteDropEndFunction.end}
									/>
								</VerticalSpacing>
							</div>
							<div className={styles.canvas}>
								<TabsComponent
									id={"$"}
									config={modelJson}
									completeFormModel={modelJson}
									currentTab={currentTab}
									setOnPaletteDropEndFunction={setOnPaletteDropEndFunction}
									onChange={setModelJson}
								/>
							</div>
						</div>
						<DragLayer/>
					</DndProvider>
				</Route>
			</Switch>
		</>
	) : (
		<LoadingIndicator/>
	);
}

export default SimplifiedFormBuilderView;
